import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

/**
 * <aphp-feedback-button> — bouton « Signaler un bug / Suggérer » embarqué
 * dans Meet, Docs et Drive.
 *
 * Deux modes selon le contexte :
 *
 *   - `mode="embed"` (defaut) : ouvre un mini-formulaire local et POST direct
 *     à l'API feedback. À utiliser quand l'embed host partage le même cookie
 *     session OIDC que l'API (ex: board lui-même).
 *
 *   - `mode="redirect"` : ouvre `<board-url>/?new=1&source=...&title=...` dans
 *     une popup. À utiliser depuis Meet/Docs/Drive — pas de problème d'auth
 *     cross-origin (l'API n'est joignable que via le board, sous oauth2-proxy).
 *
 * Attributs :
 *   - api-base       : URL de l'API feedback (mode embed seulement).
 *   - board-url      : URL du board (obligatoire en mode redirect).
 *   - source         : label remonté dans le ticket (`meet|docs|drive|board`).
 *   - label          : texte du bouton (defaut « Feedback »).
 *   - position       : `bottom-right` | `bottom-left` | `inline` (defaut `inline`).
 *   - mode           : `embed` | `redirect` (defaut `embed`).
 *   - context-title  : (mode redirect) titre pré-rempli passé au board.
 *   - context-info   : (mode redirect) description pré-remplie (ex: contexte visio).
 */
type Type = 'bug' | 'feature' | 'run' | 'build'
type Priority = 'p0' | 'p1' | 'p2' | 'p3'

@customElement('aphp-feedback-button')
export class AphpFeedbackButton extends LitElement {
  @property({ type: String, attribute: 'api-base' }) apiBase = ''
  @property({ type: String }) source = 'unknown'
  @property({ type: String }) label = 'Feedback'
  @property({ type: String }) position: 'bottom-right' | 'bottom-left' | 'inline' = 'inline'
  @property({ type: String, attribute: 'board-url' }) boardUrl = ''
  @property({ type: String }) mode: 'embed' | 'redirect' = 'embed'
  @property({ type: String, attribute: 'context-title' }) contextTitle = ''
  @property({ type: String, attribute: 'context-info' }) contextInfo = ''
  /**
   * Si "1" / "true", rend un 2e bouton "📸 Capture" à côté du feedback.
   * Ce 2e bouton ouvre `board-url?autocapture=1` (le board met alors la
   * capture d'écran en évidence — UX dédiée bug report visuel).
   * Mode `redirect` uniquement.
   */
  @property({ type: String, attribute: 'with-capture' }) withCapture = ''

  @state() private _open = false
  @state() private _title = ''
  @state() private _description = ''
  @state() private _type: Type = 'bug'
  @state() private _priority: Priority = 'p2'
  @state() private _busy = false
  @state() private _error: string | null = null
  @state() private _success: { ticketId: string } | null = null

  static override styles = css`
    :host {
      font-family: 'Marianne', system-ui, sans-serif;
      display: inline-block;
    }
    /* Position fixed portée par le :host lui-même : un parent React avec
       transform/filter (Panda CSS, Mantine, Framer) crée un nouveau
       containing block pour les fixed descendants — si on positionne via
       un span enfant, le bouton se retrouve à 0×0 hors viewport. En
       l'attachant au :host, on garantit que le composant est l'élément
       fixed, qu'aucun ancêtre ne peut capturer. */
    :host([position='bottom-right']) {
      position: fixed;
      bottom: 1rem;
      right: 1rem;
      z-index: 2147483000;
    }
    :host([position='bottom-left']) {
      position: fixed;
      bottom: 1rem;
      left: 1rem;
      z-index: 2147483000;
    }
    :host([position='inline']) {
      display: inline-block;
      position: relative;
    }
    .anchor { display: inline-block; position: relative; }

    .triggers {
      display: inline-flex;
      gap: 0.4rem;
    }
    button.trigger {
      background: #000091;
      color: #fff;
      border: none;
      border-radius: 999px;
      padding: 0.5rem 1rem;
      font-weight: 500;
      cursor: pointer;
      font-family: inherit;
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
      transition: background 80ms;
    }
    button.trigger:hover {
      background: #1212ff;
    }
    button.trigger.capture {
      background: #18753c;
    }
    button.trigger.capture:hover {
      background: #126130;
    }
    button.trigger .icon { font-size: 1rem; line-height: 1; }

    /* ---- Panel ---- */
    .panel {
      position: absolute;
      bottom: 100%;
      margin-bottom: 0.6rem;
      width: 380px;
      max-width: calc(100vw - 2rem);
      background: #fff;
      border: 1px solid #e3e3e3;
      border-radius: 8px;
      padding: 0;
      box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
      z-index: 10000;
      overflow: hidden;
      animation: panelIn 120ms ease-out;
    }
    :host([position='bottom-right']) .panel { right: 0; }
    :host([position='bottom-left']) .panel { left: 0; }
    :host([position='inline']) .panel { left: 0; top: 100%; bottom: auto; margin-top: 0.6rem; }
    @keyframes panelIn {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .panel header.head {
      background: #000091;
      color: #fff;
      padding: 0.7rem 0.85rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .panel header.head h3 {
      margin: 0;
      font-size: 0.95rem;
      font-weight: 500;
    }
    .panel header.head .src {
      font-size: 0.7rem;
      opacity: 0.75;
      font-family: ui-monospace, Menlo, monospace;
    }
    button.close {
      background: transparent;
      border: none;
      color: #fff;
      cursor: pointer;
      font-size: 1.2rem;
      line-height: 1;
      padding: 0 0.2rem;
      opacity: 0.8;
    }
    button.close:hover { opacity: 1; }

    .form {
      padding: 0.85rem;
    }
    label {
      display: block;
      font-size: 0.8rem;
      color: #555;
      margin: 0.6rem 0 0.25rem;
      font-weight: 500;
    }
    label:first-of-type { margin-top: 0; }
    input[type='text'],
    textarea {
      width: 100%;
      padding: 0.45rem 0.55rem;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-family: inherit;
      font-size: 0.9rem;
      box-sizing: border-box;
      transition: border-color 80ms;
    }
    input[type='text']:focus,
    textarea:focus {
      outline: none;
      border-color: #000091;
      box-shadow: 0 0 0 2px rgba(0, 0, 145, 0.15);
    }
    textarea {
      min-height: 70px;
      max-height: 200px;
      resize: vertical;
    }

    /* Chip groups (type + priority) */
    .chips {
      display: flex;
      gap: 0.3rem;
      flex-wrap: wrap;
    }
    .chip {
      background: #f6f6f6;
      border: 1px solid #e3e3e3;
      color: #555;
      padding: 0.3rem 0.7rem;
      border-radius: 4px;
      cursor: pointer;
      font-size: 0.8rem;
      font-family: inherit;
      transition: all 80ms;
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
    }
    .chip:hover {
      border-color: #000091;
    }
    .chip.active {
      background: #000091;
      color: #fff;
      border-color: #000091;
    }
    .chip.prio-p0.active { background: #ce0500; border-color: #ce0500; }
    .chip.prio-p1.active { background: #b34000; border-color: #b34000; }
    .chip.prio-p3.active { background: #888; border-color: #888; }

    .actions {
      margin-top: 0.9rem;
      display: flex;
      gap: 0.5rem;
    }
    button.primary {
      background: #000091;
      color: #fff;
      border: none;
      border-radius: 4px;
      padding: 0.5rem 0.9rem;
      cursor: pointer;
      font-weight: 500;
      flex: 1;
      font-family: inherit;
      transition: background 80ms;
    }
    button.primary:hover:not(:disabled) {
      background: #1212ff;
    }
    button.primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* ---- States ---- */
    .error {
      background: #fff4f4;
      color: #ce0500;
      padding: 0.5rem 0.65rem;
      border: 1px solid #ffcccc;
      border-radius: 4px;
      font-size: 0.85rem;
      margin: 0 0.85rem 0;
    }
    .success-card {
      padding: 1.5rem 1rem;
      text-align: center;
    }
    .success-card .check {
      width: 48px;
      height: 48px;
      margin: 0 auto 0.6rem;
      border-radius: 50%;
      background: #18753c;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.5rem;
      animation: pop 280ms cubic-bezier(0.18, 1.6, 0.5, 1);
    }
    @keyframes pop {
      0%   { transform: scale(0); }
      50%  { transform: scale(1.15); }
      100% { transform: scale(1); }
    }
    .success-card h4 {
      margin: 0 0 0.3rem;
      color: #1e1e1e;
      font-weight: 500;
    }
    .success-card .ticket-id {
      font-family: ui-monospace, Menlo, monospace;
      font-size: 0.8rem;
      color: #888;
      margin-bottom: 0.75rem;
    }
    .success-card .actions-success {
      display: flex;
      gap: 0.4rem;
      justify-content: center;
    }
    .success-card a,
    .success-card button.secondary {
      background: #fff;
      color: #000091;
      border: 1px solid #000091;
      padding: 0.35rem 0.8rem;
      border-radius: 4px;
      text-decoration: none;
      font-size: 0.85rem;
      cursor: pointer;
      font-family: inherit;
    }
    .success-card a:hover,
    .success-card button.secondary:hover {
      background: #e3e3fd;
    }
  `

  private _resetForm() {
    this._title = ''
    this._description = ''
    this._type = 'bug'
    this._priority = 'p2'
    this._error = null
    this._success = null
  }

  private async _submit() {
    if (!this._title.trim()) {
      this._error = 'Titre requis.'
      return
    }
    this._busy = true
    this._error = null
    try {
      const base = this.apiBase.replace(/\/$/, '')
      const res = await fetch(`${base}/api/tickets/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          title: this._title,
          description: this._description,
          type: this._type,
          priority: this._priority,
          source: this.source,
        }),
      })
      if (!res.ok) {
        const body = await res.text().catch(() => '')
        throw new Error(
          `HTTP ${res.status}${body ? ` — ${body.slice(0, 120)}` : ''}`,
        )
      }
      const data = (await res.json()) as { id: string }
      this._success = { ticketId: data.id }
    } catch (err) {
      this._error = err instanceof Error ? err.message : String(err)
    } finally {
      this._busy = false
    }
  }

  private _renderChip(
    group: 'type' | 'priority',
    value: Type | Priority,
    label: string,
    icon = '',
  ) {
    const active = group === 'type' ? this._type === value : this._priority === value
    const cls = group === 'priority' ? `chip prio-${value} ${active ? 'active' : ''}` : `chip ${active ? 'active' : ''}`
    return html`<button
      type="button"
      class=${cls}
      ?disabled=${this._busy}
      @click=${() => {
        if (group === 'type') this._type = value as Type
        else this._priority = value as Priority
      }}
    >${icon} ${label}</button>`
  }

  /**
   * Ouvre le board dans une popup avec les query params de pré-remplissage.
   * En mode redirect, l'auth est gérée par le board (oauth2-proxy + Keycloak),
   * et le user est probablement déjà loggué via SSO partagé.
   *
   * Si `withCapture=true`, on ajoute `autocapture=1` au query string — le
   * board met alors le bouton "📸 Capturer mon écran" en évidence pour un
   * workflow rapide (idéal pour signaler un bug visuel depuis la visio).
   */
  private _openRedirect(opts: { capture?: boolean } = {}) {
    if (!this.boardUrl) return
    const url = new URL(this.boardUrl)
    url.searchParams.set('new', '1')
    url.searchParams.set('source', this.source)
    if (this.contextTitle) url.searchParams.set('title', this.contextTitle)
    if (this.contextInfo) url.searchParams.set('description', this.contextInfo)
    if (opts.capture) url.searchParams.set('autocapture', '1')
    const w = 600
    const h = 700
    const left = (window.screen.width - w) / 2
    const top = (window.screen.height - h) / 2
    window.open(
      url.toString(),
      'aphp-feedback-board',
      `width=${w},height=${h},left=${left},top=${top},popup=yes`,
    )
  }

  private get _withCaptureEnabled(): boolean {
    return this.withCapture === '1' || this.withCapture === 'true'
  }

  override render() {
    const onClick = this.mode === 'redirect'
      ? () => this._openRedirect()
      : () => (this._open = !this._open)
    // Le positionnement est porté par le :host via attribute selectors —
    // le span anchor reste relatif pour ancrer le panel (.panel uses bottom: 100%).
    const anchorClass = 'anchor'
    // Le 2e bouton "Capture" n'a de sens qu'en mode redirect (workflow board) ;
    // en mode embed le panel local n'a pas de notion d'autocapture.
    const showCapture = this._withCaptureEnabled && this.mode === 'redirect'
    return html`
      <span class=${anchorClass}>
        <span class="triggers">
          <button class="trigger" @click=${onClick}>
            <span class="icon">💬</span> ${this.label}
          </button>
          ${showCapture
            ? html`<button
                class="trigger capture"
                title="Capturer mon écran et créer un ticket"
                @click=${() => this._openRedirect({ capture: true })}
              >
                <span class="icon">📸</span> Capture
              </button>`
            : nothing}
        </span>
        ${this._open
          ? html`
              <div class="panel">
                <header class="head">
                  <h3>Nouveau ticket</h3>
                  <span class="src">${this.source}</span>
                  <button
                    class="close"
                    title="Fermer"
                    @click=${() => {
                      this._open = false
                      if (this._success) this._resetForm()
                    }}
                  >×</button>
                </header>

                ${this._success
                  ? html`
                      <div class="success-card">
                        <div class="check">✓</div>
                        <h4>Ticket envoyé, merci !</h4>
                        <div class="ticket-id">#${this._success.ticketId.slice(0, 8)}</div>
                        <div class="actions-success">
                          ${this.boardUrl
                            ? html`<a href=${this.boardUrl} target="_blank" rel="noopener">
                                Voir le board
                              </a>`
                            : nothing}
                          <button class="secondary" @click=${this._resetForm}>
                            Nouveau ticket
                          </button>
                        </div>
                      </div>
                    `
                  : html`
                      ${this._error
                        ? html`<div class="error">${this._error}</div>`
                        : nothing}
                      <div class="form">
                        <label>Titre *</label>
                        <input
                          type="text"
                          placeholder="Décrivez en une phrase…"
                          .value=${this._title}
                          @input=${(e: Event) =>
                            (this._title = (e.target as HTMLInputElement).value)}
                          ?disabled=${this._busy}
                          maxlength="200"
                        />

                        <label>Description (optionnel, markdown supporté)</label>
                        <textarea
                          placeholder="Étapes pour reproduire, contexte, suggestions…"
                          .value=${this._description}
                          @input=${(e: Event) =>
                            (this._description = (e.target as HTMLTextAreaElement).value)}
                          ?disabled=${this._busy}
                        ></textarea>

                        <label>Type</label>
                        <div class="chips">
                          ${this._renderChip('type', 'bug', 'Bug', '🐛')}
                          ${this._renderChip('type', 'feature', 'Feature', '✨')}
                          ${this._renderChip('type', 'run', 'Feedback', '📣')}
                          ${this._renderChip('type', 'build', 'Build', '🛠')}
                        </div>

                        <label>Priorité</label>
                        <div class="chips">
                          ${this._renderChip('priority', 'p0', 'P0 Bloquant')}
                          ${this._renderChip('priority', 'p1', 'P1 Critique')}
                          ${this._renderChip('priority', 'p2', 'P2 Normal')}
                          ${this._renderChip('priority', 'p3', 'P3 Mineur')}
                        </div>

                        <div class="actions">
                          <button
                            class="primary"
                            @click=${this._submit}
                            ?disabled=${this._busy}
                          >${this._busy ? 'Envoi…' : 'Envoyer le ticket'}</button>
                        </div>
                      </div>
                    `}
              </div>
            `
          : nothing}
      </span>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'aphp-feedback-button': AphpFeedbackButton
  }
}
